///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2022
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "../../Modal";
import FileDropper from "../../FileDropper";
import { fileTypes, httpResponseStatus } from "../../../helpers/types";
import FileIcon from "../../form-controls/FileIcon";
import { postLanes } from "../../../api/lanes";
import saveFile from "../../../helpers/saveFile";
import { fetchBidSummary } from "../../../slices/bidSummarySlice";
import {
  checkLaneImportInProgress,
  checkLaneRepriceInProgress,
  showImportInProgressModal,
  showRepriceInProgressModal
} from "../../../slices/bidPricingSlice";
import Alert from "../../Alert";
import Spinner from "../../icons/Spinner";
import messages from "../../../helpers/messages";

export const UploadLanesModal = ({
  bidId,
  handleClose,
  role,
  fetchAfterUpload,
  modalTitle,
  modalMessage,
  showOnSuccessModal,
  onSuccess,
  progressMessage,
  uploadButtonCaption,
  validationMessage,
  isLaneImport,
  openedFromBanner,
  uploadApi = postLanes
}) => {
  const defaultSystemErrorMessage =
    messages.uploadLaneSystemErrorMessage.defaultMessage;
  const { supportedFiles } = useSelector(state => state.app).supportedFiles;
  const { summary } = useSelector(state => state.bidSummary);
  const { user } = useSelector(state => state.user);
  const [successModal, toggleSuccessModal] = useState(false);
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [systemError, toggleSystemError] = useState(false);
  const [validation, setValidation] = useState({
    isValid: true,
    errorFile: null
  });
  const [triedAgain, setTriedAgain] = useState(false);
  const [systemErrorMessage, setSystemErrorMessage] = useState(
    defaultSystemErrorMessage
  );

  const handleDrop = files => setFile(files[0]);
  const handleBack = () => setFile(null);
  const onUploadProgress = ({ loaded, total }) =>
    setProgress(Math.floor((loaded / total) * 100));

  const upload = () => {
    setSystemErrorMessage(defaultSystemErrorMessage);
    toggleSystemError(false);
    uploadApi(
      bidId,
      file,
      {
        onUploadProgress,
        responseType: isLaneImport ? "" : "blob"
      },
      user.userRoleForManagementUser
    )
      .then(({ headers, data }) => {
        if (
          supportedFiles.some(
            s =>
              s.fileType === fileTypes.excel &&
              s.mimeType === headers["content-type"]
          )
        ) {
          setValidation({ isValid: false, errorFile: data });
          setProgress(null);
        } else {
          if (showOnSuccessModal === false) {
            onSuccess && onSuccess();
          } else {
            toggleSuccessModal(true);
          }
        }
      })
      .catch(e => {
        if (
          isLaneImport &&
          e &&
          e.response &&
          e.response.status === httpResponseStatus.badRequest
        ) {
          e.response.data && setSystemErrorMessage(e.response.data);
        }
        toggleSystemError(true);
        setProgress(null);
      });
  };

  const laneRepriceCallbackSuccess = value => {
    if (value) {
      !openedFromBanner && handleModalClose();
      dispatch(showRepriceInProgressModal(true));
    } else {
      upload();
    }
  };

  const laneImportCallbackSuccess = value => {
    if (value) {
      !openedFromBanner && handleModalClose();
      dispatch(showImportInProgressModal(true));
    } else {
      dispatch(
        checkLaneRepriceInProgress(bidId, laneRepriceCallbackSuccess, () =>
          upload()
        )
      );
    }
  };

  const handleUpload = () => {
    if (isLaneImport) {
      dispatch(
        checkLaneImportInProgress(bidId, laneImportCallbackSuccess, () =>
          upload()
        )
      );
    } else {
      upload();
    }
  };

  const getTitle = () => {
    if (!validation.isValid) {
      return messages.validationError.defaultMessage;
    } else if (systemError === true) {
      return messages.systemError.defaultMessage;
    } else if (progress === null || isLaneImport) {
      return modalTitle;
    } else {
      return messages.processing.defaultMessage;
    }
  };

  const downloadLaneErrors = () => {
    saveFile(validation.errorFile, `${file.name.split(".")[0]} Errors.xlsx`);
  };

  const getBodyMessage = () => {
    if (systemError === true) {
      return systemErrorMessage;
    } else if (validation.isValid === true) {
      return modalMessage;
    } else {
      return validationMessage;
    }
  };

  const handleReset = () => {
    setFile(null);
    toggleSystemError(false);
  };

  const handleModalClose = refresh => {
    const fetchDetails =
      refresh === true ||
      !validation.isValid ||
      systemError === true ||
      triedAgain;
    if (fetchDetails && fetchAfterUpload) {
      dispatch(
        fetchBidSummary({
          bidId,
          role,
          fetchTLLaneCount: false,
          division: summary?.division,
          fetchLanes: true,
          shouldFetchMatchingLanes: true
        })
      );
    }
    handleClose(fetchDetails);
  };

  const handleTryAgain = () => {
    setValidation({ isValid: true, errorFile: null });
    setFile(null);
    setTriedAgain(true);
  };

  const startPollingForLaneImportProgress = () => {
    dispatch(checkLaneImportInProgress(bidId));
    handleModalClose(true);
  };

  return (
    <>
      <Alert
        title={messages.processing.defaultMessage}
        message={messages.laneInImportProgressModalMessage.defaultMessage}
        handleClose={startPollingForLaneImportProgress}
        open={successModal}
        large={true}
        singleButtonCaption={messages.done.defaultMessage}
      />
      <Modal
        title={getTitle()}
        open={!successModal}
        handleClose={handleModalClose}
        titleClass="modal__title_light uppercase"
        closeButtonClass={
          "modal__close-button mr-24 mt-16 " + (progress !== null && "hidden")
        }
        bodyClass="border-top-grey p-24"
        large={true}
      >
        {progress === null || isLaneImport ? (
          <Fragment>
            <p className="mb-24 font-size-16">{getBodyMessage()}</p>
            <FileDropper
              data-testid="upload-lane-modal"
              boxClassName="py-36"
              handleDrop={handleDrop}
              style={{ width: 446 }}
              multiple={false}
              acceptedFileTypes={[fileTypes.excel]}
              show={file === null}
            >
              <span className="zeta">
                {messages.uploadYourFilesHereOr.defaultMessage}
              </span>
              <button id="choose-file" className="underline no-focus cursor">
                {messages.browse.defaultMessage}
              </button>
            </FileDropper>

            {!systemError && file !== null && (
              <div className="pos-relative">
                <FileIcon
                  className="mr-12 mt-6"
                  mimeType={file.type}
                  width="18px"
                  height="22px"
                />
                <span
                  style={{ marginRight: validation.isValid ? undefined : 120 }}
                  data-testid="file-name"
                  className="pos-absolute mt-8 weight--semibold"
                >
                  {validation.isValid
                    ? file.name
                    : `${file.name.split(".")[0]} Errors.xlsx`}
                </span>
                {validation.isValid || (
                  <button
                    data-testid="download-error-file"
                    onClick={downloadLaneErrors}
                    className="text-button fr univers-67-bold-cond"
                  >
                    {messages.download.defaultMessage}
                  </button>
                )}
              </div>
            )}

            {validation.isValid && (
              <div>
                {!systemError ? (
                  <Fragment>
                    <button
                      data-testid="upload"
                      disabled={file === null || (progress && isLaneImport)}
                      onClick={handleUpload}
                      className="primary-button fr univers-67-bold-cond mt-20 mb-24 px-16"
                    >
                      {progress && isLaneImport ? (
                        <Spinner
                          className="spinner"
                          fill="#ffffff"
                          height="16px"
                          width="16px"
                        />
                      ) : (
                        uploadButtonCaption
                      )}
                    </button>
                    <button
                      data-testid="back"
                      onClick={handleBack}
                      disabled={progress && isLaneImport}
                      className={
                        "secondary-button fr univers-67-bold-cond mr-16 mt-20 mb-24 " +
                        (file === null ? " hidden" : "")
                      }
                    >
                      {messages.back.defaultMessage}
                    </button>
                  </Fragment>
                ) : (
                  <Fragment>
                    <button
                      data-testid="try-again"
                      onClick={handleUpload}
                      className="primary-button fr univers-67-bold-cond mb-24"
                    >
                      {messages.tryAgain.defaultMessage}
                    </button>
                    <button
                      data-testid="cancel"
                      onClick={handleReset}
                      className="secondary-button fr univers-67-bold-cond mr-16 mb-24 "
                    >
                      {messages.cancel.defaultMessage}
                    </button>
                  </Fragment>
                )}
              </div>
            )}
            {validation.isValid || (
              <div className="mt-12">
                <button
                  data-testid="try-again"
                  onClick={handleTryAgain}
                  className="primary-button fr univers-67-bold-cond mt-20 mb-24"
                >
                  {messages.tryAgain.defaultMessage}
                </button>
              </div>
            )}
          </Fragment>
        ) : (
          <Fragment>
            <p className="mb-24 font-size-16">{progressMessage}</p>
            <div className="h-12 file-progress-bar">
              <div
                style={{ width: (progress || "0") + "%" }}
                className={`h-12 file-progress-complete ${
                  progress === 100 ? "processing" : ""
                }`}
              >
                &nbsp;
              </div>
            </div>
          </Fragment>
        )}
      </Modal>
    </>
  );
};

UploadLanesModal.defaultProps = {
  fetchAfterUpload: true,
  modalTitle: messages.laneImport.defaultMessage,
  modalMessage:
    messages.selectAPopulatedRfqTemplateToImportLanes.defaultMessage,
  showOnSuccessModal: true,
  progressMessage: messages.uploadLaneProgressMessage.defaultMessage,
  uploadButtonCaption: messages.importLanes.defaultMessage,
  validationMessage: messages.uploadLaneValidationMessage.defaultMessage,
  isLaneImport: false,
  openedFromBanner: false
};

export default UploadLanesModal;
